// @ts-nocheck
import React from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  TrendingUp,
  Calendar,
  MessageSquare,
  ChevronRight,
  BarChart3
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useTeams } from "@/hooks/api-hooks";

export function MyTeamPage() {
  const { user } = useAuth();

  // For staff users, we need to pass memberOfTeams=true to see only teams they're members of
  // For owner/admin users, they can see all teams without this parameter
  const isOrgAdminOrOwner = user?.organizationRole === 'owner' || user?.organizationRole === 'admin';
  const teamsParams = isOrgAdminOrOwner
    ? { includeMembers: true }
    : { memberOfTeams: true, includeMembers: true };

  const { data: teamsData, isLoading: isLoadingTeams } = useTeams(teamsParams);
  const teams = teamsData?.teams || [];

  // Show loading state
  if (isLoadingTeams) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground"></div>
          <span className="text-muted-foreground">Loading teams...</span>
        </div>
      </div>
    );
  }

  // Calculate overall stats
  const totalMembers = teams.reduce((sum, team) => sum + (team.memberCount || 0), 0);
  const averageTemperature = teams.length > 0
    ? teams.reduce((sum, team) => sum + (team.averageRating || 0), 0) / teams.length
    : 0;
  const totalFeedback = teams.reduce((sum, team) => sum + (team.totalFeedback || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl flex items-center justify-center">
                <Users className="h-8 w-8 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">My Teams</h1>
                <p className="text-muted-foreground text-lg">
                  Overview of all teams {isOrgAdminOrOwner ? "under your management" : "you're a member of"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground mb-2">{teams.length}</div>
                <div className="text-muted-foreground">Total Teams</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground mb-2">{totalMembers}</div>
                <div className="text-muted-foreground">Total Members</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground mb-2">{averageTemperature.toFixed(1)}</div>
                <div className="text-muted-foreground">Avg Temperature</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Teams List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Teams</CardTitle>
            <CardDescription>
              {isOrgAdminOrOwner
                ? "Manage and monitor all teams under your supervision"
                : "Teams you're a member of"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teams.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-muted/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">No Teams Yet</h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  You don't have any teams assigned to you yet. Contact your administrator to get team access.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {teams.map((team) => (
                  <Link key={team.id} to={`/team/${team.id}`}>
                    <div className="p-6 rounded-lg border border-border hover:border-border/80 transition-colors cursor-pointer hover:shadow-sm">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                            <Users className="h-6 w-6 text-indigo-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-foreground">{team.name}</h3>
                              <Badge variant="secondary">
                                {team.memberCount || 0} members
                              </Badge>
                            </div>
                            <p className="text-muted-foreground">{team.description}</p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>

                      {/* Team Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="text-xs text-muted-foreground mb-1">Temperature</div>
                          <div className="font-medium text-foreground text-sm">
                            {team.averageRating ? team.averageRating.toFixed(1) : '0.0'}
                          </div>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="text-xs text-muted-foreground mb-1">Feedback</div>
                          <div className="font-medium text-foreground text-sm">{team.totalFeedback || 0}</div>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="text-xs text-muted-foreground mb-1">1-on-1s</div>
                          <div className="font-medium text-foreground text-sm">{team.totalSessions || 0}</div>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="text-xs text-muted-foreground mb-1">Status</div>
                          <div className="font-medium text-foreground text-sm">
                            <Badge
                              variant={team.isActive ? "default" : "secondary"}
                              className="text-xs"
                            >
                              {team.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {isOrgAdminOrOwner && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/team/create">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Users className="h-4 w-4" />
                Create New Team
              </Button>
            </Link>
            <Link to="/team/analytics">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <BarChart3 className="h-4 w-4" />
                View Analytics
              </Button>
            </Link>
            <Link to="/one-on-ones">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Calendar className="h-4 w-4" />
                Manage 1-on-1s
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
